import React, { useState, useEffect } from 'react';
import { User, Calendar, Globe, Mail } from 'lucide-react';
import Select from 'react-select';
import { ApplicationFormData, ApplicationFormErrors } from '@/types/application';
import { agentApi } from '@/services/agentApi';

interface PersonalInfoStepProps {
  formData: ApplicationFormData;
  errors: ApplicationFormErrors;
  updateFormData: (updates: Partial<ApplicationFormData>) => void;
  setErrors: (errors: ApplicationFormErrors) => void;
}

const GENDERS = ['Male', 'Female', 'Other', 'Prefer not to say'];
const MARITAL_STATUSES = ['Single', 'Married', 'Divorced', 'Widowed', 'Separated'];
const ETHNICITIES = [
  'African',
  'Asian',
  'European',
  'Latin American',
  'Māori',
  'Middle Eastern',
  'Pacific Peoples',
  'Other'
];
const COUNTRIES: Array<{ code: string; name: string }> = [
  { code: 'AF', name: 'Afghanistan' }, { code: 'AL', name: 'Albania' }, { code: 'DZ', name: 'Algeria' },
  { code: 'AD', name: 'Andorra' }, { code: 'AO', name: 'Angola' }, { code: 'AG', name: 'Antigua and Barbuda' },
  { code: 'AR', name: 'Argentina' }, { code: 'AM', name: 'Armenia' }, { code: 'AU', name: 'Australia' },
  { code: 'AT', name: 'Austria' }, { code: 'AZ', name: 'Azerbaijan' }, { code: 'BS', name: 'Bahamas' },
  { code: 'BH', name: 'Bahrain' }, { code: 'BD', name: 'Bangladesh' }, { code: 'BB', name: 'Barbados' },
  { code: 'BY', name: 'Belarus' }, { code: 'BE', name: 'Belgium' }, { code: 'BZ', name: 'Belize' },
  { code: 'BJ', name: 'Benin' }, { code: 'BT', name: 'Bhutan' }, { code: 'BO', name: 'Bolivia' },
  { code: 'BA', name: 'Bosnia and Herzegovina' }, { code: 'BW', name: 'Botswana' }, { code: 'BR', name: 'Brazil' },
  { code: 'BN', name: 'Brunei' }, { code: 'BG', name: 'Bulgaria' }, { code: 'BF', name: 'Burkina Faso' },
  { code: 'BI', name: 'Burundi' }, { code: 'CV', name: 'Cabo Verde' }, { code: 'KH', name: 'Cambodia' },
  { code: 'CM', name: 'Cameroon' }, { code: 'CA', name: 'Canada' }, { code: 'CF', name: 'Central African Republic' },
  { code: 'TD', name: 'Chad' }, { code: 'CL', name: 'Chile' }, { code: 'CN', name: 'China' },
  { code: 'CO', name: 'Colombia' }, { code: 'KM', name: 'Comoros' }, { code: 'CG', name: 'Congo' },
  { code: 'CR', name: 'Costa Rica' }, { code: 'CI', name: "Cote d'Ivoire" }, { code: 'HR', name: 'Croatia' },
  { code: 'CU', name: 'Cuba' }, { code: 'CY', name: 'Cyprus' }, { code: 'CZ', name: 'Czechia' },
  { code: 'CD', name: 'Democratic Republic of the Congo' }, { code: 'DK', name: 'Denmark' }, { code: 'DJ', name: 'Djibouti' },
  { code: 'DM', name: 'Dominica' }, { code: 'DO', name: 'Dominican Republic' }, { code: 'EC', name: 'Ecuador' },
  { code: 'EG', name: 'Egypt' }, { code: 'SV', name: 'El Salvador' }, { code: 'GQ', name: 'Equatorial Guinea' },
  { code: 'ER', name: 'Eritrea' }, { code: 'EE', name: 'Estonia' }, { code: 'SZ', name: 'Eswatini' },
  { code: 'ET', name: 'Ethiopia' }, { code: 'FJ', name: 'Fiji' }, { code: 'FI', name: 'Finland' },
  { code: 'FR', name: 'France' }, { code: 'GA', name: 'Gabon' }, { code: 'GM', name: 'Gambia' },
  { code: 'GE', name: 'Georgia' }, { code: 'DE', name: 'Germany' }, { code: 'GH', name: 'Ghana' },
  { code: 'GR', name: 'Greece' }, { code: 'GD', name: 'Grenada' }, { code: 'GT', name: 'Guatemala' },
  { code: 'GN', name: 'Guinea' }, { code: 'GW', name: 'Guinea-Bissau' }, { code: 'GY', name: 'Guyana' },
  { code: 'HT', name: 'Haiti' }, { code: 'HN', name: 'Honduras' }, { code: 'HU', name: 'Hungary' },
  { code: 'IS', name: 'Iceland' }, { code: 'IN', name: 'India' }, { code: 'ID', name: 'Indonesia' },
  { code: 'IR', name: 'Iran' }, { code: 'IQ', name: 'Iraq' }, { code: 'IE', name: 'Ireland' },
  { code: 'IL', name: 'Israel' }, { code: 'IT', name: 'Italy' }, { code: 'JM', name: 'Jamaica' },
  { code: 'JP', name: 'Japan' }, { code: 'JO', name: 'Jordan' }, { code: 'KZ', name: 'Kazakhstan' },
  { code: 'KE', name: 'Kenya' }, { code: 'KI', name: 'Kiribati' }, { code: 'KW', name: 'Kuwait' },
  { code: 'KG', name: 'Kyrgyzstan' }, { code: 'LA', name: 'Laos' }, { code: 'LV', name: 'Latvia' },
  { code: 'LB', name: 'Lebanon' }, { code: 'LS', name: 'Lesotho' }, { code: 'LR', name: 'Liberia' },
  { code: 'LY', name: 'Libya' }, { code: 'LI', name: 'Liechtenstein' }, { code: 'LT', name: 'Lithuania' },
  { code: 'LU', name: 'Luxembourg' }, { code: 'MG', name: 'Madagascar' }, { code: 'MW', name: 'Malawi' },
  { code: 'MY', name: 'Malaysia' }, { code: 'MV', name: 'Maldives' }, { code: 'ML', name: 'Mali' },
  { code: 'MT', name: 'Malta' }, { code: 'MH', name: 'Marshall Islands' }, { code: 'MR', name: 'Mauritania' },
  { code: 'MU', name: 'Mauritius' }, { code: 'MX', name: 'Mexico' }, { code: 'FM', name: 'Micronesia' },
  { code: 'MD', name: 'Moldova' }, { code: 'MC', name: 'Monaco' }, { code: 'MN', name: 'Mongolia' },
  { code: 'ME', name: 'Montenegro' }, { code: 'MA', name: 'Morocco' }, { code: 'MZ', name: 'Mozambique' },
  { code: 'MM', name: 'Myanmar' }, { code: 'NA', name: 'Namibia' }, { code: 'NR', name: 'Nauru' },
  { code: 'NP', name: 'Nepal' }, { code: 'NL', name: 'Netherlands' }, { code: 'NZ', name: 'New Zealand' },
  { code: 'NI', name: 'Nicaragua' }, { code: 'NE', name: 'Niger' }, { code: 'NG', name: 'Nigeria' },
  { code: 'KP', name: 'North Korea' }, { code: 'MK', name: 'North Macedonia' }, { code: 'NO', name: 'Norway' },
  { code: 'OM', name: 'Oman' }, { code: 'PK', name: 'Pakistan' }, { code: 'PW', name: 'Palau' },
  { code: 'PS', name: 'Palestine' }, { code: 'PA', name: 'Panama' }, { code: 'PG', name: 'Papua New Guinea' },
  { code: 'PY', name: 'Paraguay' }, { code: 'PE', name: 'Peru' }, { code: 'PH', name: 'Philippines' },
  { code: 'PL', name: 'Poland' }, { code: 'PT', name: 'Portugal' }, { code: 'QA', name: 'Qatar' },
  { code: 'RO', name: 'Romania' }, { code: 'RU', name: 'Russia' }, { code: 'RW', name: 'Rwanda' },
  { code: 'KN', name: 'Saint Kitts and Nevis' }, { code: 'LC', name: 'Saint Lucia' },
  { code: 'VC', name: 'Saint Vincent and the Grenadines' }, { code: 'WS', name: 'Samoa' },
  { code: 'SM', name: 'San Marino' }, { code: 'ST', name: 'Sao Tome and Principe' }, { code: 'SA', name: 'Saudi Arabia' },
  { code: 'SN', name: 'Senegal' }, { code: 'RS', name: 'Serbia' }, { code: 'SC', name: 'Seychelles' },
  { code: 'SL', name: 'Sierra Leone' }, { code: 'SG', name: 'Singapore' }, { code: 'SK', name: 'Slovakia' },
  { code: 'SI', name: 'Slovenia' }, { code: 'SB', name: 'Solomon Islands' }, { code: 'SO', name: 'Somalia' },
  { code: 'ZA', name: 'South Africa' }, { code: 'KR', name: 'South Korea' }, { code: 'SS', name: 'South Sudan' },
  { code: 'ES', name: 'Spain' }, { code: 'LK', name: 'Sri Lanka' }, { code: 'SD', name: 'Sudan' },
  { code: 'SR', name: 'Suriname' }, { code: 'SE', name: 'Sweden' }, { code: 'CH', name: 'Switzerland' },
  { code: 'SY', name: 'Syria' }, { code: 'TJ', name: 'Tajikistan' }, { code: 'TZ', name: 'Tanzania' },
  { code: 'TH', name: 'Thailand' }, { code: 'TL', name: 'Timor-Leste' }, { code: 'TG', name: 'Togo' },
  { code: 'TO', name: 'Tonga' }, { code: 'TT', name: 'Trinidad and Tobago' }, { code: 'TN', name: 'Tunisia' },
  { code: 'TR', name: 'Turkey' }, { code: 'TM', name: 'Turkmenistan' }, { code: 'TV', name: 'Tuvalu' },
  { code: 'UG', name: 'Uganda' }, { code: 'UA', name: 'Ukraine' }, { code: 'AE', name: 'United Arab Emirates' },
  { code: 'GB', name: 'United Kingdom' }, { code: 'US', name: 'United States' }, { code: 'UY', name: 'Uruguay' },
  { code: 'UZ', name: 'Uzbekistan' }, { code: 'VU', name: 'Vanuatu' }, { code: 'VA', name: 'Vatican City' },
  { code: 'VE', name: 'Venezuela' }, { code: 'VN', name: 'Vietnam' }, { code: 'YE', name: 'Yemen' },
  { code: 'ZM', name: 'Zambia' }, { code: 'ZW', name: 'Zimbabwe' }
];

const codeToFlag = (countryCode: string): string =>
  countryCode
    .toUpperCase()
    .replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt(0)));

const COUNTRY_OPTIONS = COUNTRIES
  .map((country) => ({
    value: country.name,
    label: `${codeToFlag(country.code)} ${country.name}`,
  }))
  .sort((a, b) => a.value.localeCompare(b.value));

export const PersonalInfoStep: React.FC<PersonalInfoStepProps> = ({
  formData,
  errors,
  updateFormData,
  setErrors,
}) => {
  const [selectedInstitution, setSelectedInstitution] = useState<any>(null);

  useEffect(() => {
    if (formData.universityId) {
      loadInstitutionInfo();
    } else {
      setSelectedInstitution(null);
    }
  }, [formData.universityId]);

  const loadInstitutionInfo = async () => {
    try {
      const institutions = await agentApi.getApprovedInstitutions();
      const institution = institutions.find(inst => inst.id === formData.universityId);
      setSelectedInstitution(institution || null);
    } catch (error) {
      console.error('Error loading institution info:', error);
      setSelectedInstitution(null);
    }
  };

  const handleChange = (field: keyof ApplicationFormData, value: any) => {
    updateFormData({ [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: undefined });
    }
  };

  // Determine dropdown value and whether to show "Other" text input
  const isPredefinedEthnicity = formData.ethnicity && ETHNICITIES.includes(formData.ethnicity);
  const hasCustomEthnicity = formData.ethnicity !== undefined && formData.ethnicity !== '' && !isPredefinedEthnicity;
  const isOtherSelected = formData.ethnicity === '' || hasCustomEthnicity;
  const ethnicityDropdownValue = isPredefinedEthnicity 
    ? formData.ethnicity 
    : (isOtherSelected ? 'Other' : '');
  const showOtherInput = ethnicityDropdownValue === 'Other';

  const institutionCountry = selectedInstitution?.country || 'New Zealand';
  const selectedCountryOption =
    COUNTRY_OPTIONS.find((option) => option.value === formData.countryOfCitizenship) || null;

  return (
    <div>
      <h5 className="fw-bold mb-3">Personal Information</h5>
      
      <div className="row">
        <div className="col-md-3 mb-2">
          <label className="form-label fw-semibold">
            Preferred Title <span className="text-danger">*</span>
          </label>
          <select
            className={`form-select ${errors.preferredTitle ? 'is-invalid' : ''}`}
            value={formData.preferredTitle || ''}
            onChange={(e) => handleChange('preferredTitle', e.target.value)}
          >
            <option value="">Select title</option>
            <option value="Mr">Mr</option>
            <option value="Mrs">Mrs</option>
            <option value="Ms">Ms</option>
            <option value="Miss">Miss</option>
            <option value="Dr">Dr</option>
            <option value="Prof">Prof</option>
          </select>
          {errors.preferredTitle && (
            <div className="invalid-feedback d-block">{errors.preferredTitle}</div>
          )}
        </div>

        <div className="col-md-3 mb-2">
          <label className="form-label fw-semibold">
            First Name <span className="text-danger">*</span>
          </label>
          <div className="input-group">
            <span className="input-group-text bg-light">
              <User size={18} className="text-muted" />
            </span>
            <input
              type="text"
              className={`form-control ${errors.firstName ? 'is-invalid' : ''}`}
              value={formData.firstName}
              onChange={(e) => handleChange('firstName', e.target.value)}
              placeholder="First name"
            />
            {errors.firstName && (
              <div className="invalid-feedback">{errors.firstName}</div>
            )}
          </div>
        </div>

        <div className="col-md-3 mb-2">
          <label className="form-label fw-semibold">Middle Name</label>
          <input
            type="text"
            className="form-control"
            value={formData.middleName || ''}
            onChange={(e) => handleChange('middleName', e.target.value)}
            placeholder="Middle name (optional)"
          />
        </div>

        <div className="col-md-3 mb-2">
          <label className="form-label fw-semibold">
            Family Name <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className={`form-control ${errors.lastName ? 'is-invalid' : ''}`}
            value={formData.lastName}
            onChange={(e) => handleChange('lastName', e.target.value)}
            placeholder="Last name"
          />
          {errors.lastName && (
            <div className="invalid-feedback">{errors.lastName}</div>
          )}
        </div>
      </div>

      <div className="row">
        <div className="col-md-6 mb-3">
          <label className="form-label fw-semibold">
            Email Address <span className="text-danger">*</span>
          </label>
          <div className="input-group">
            <span className="input-group-text bg-light">
              <Mail size={18} className="text-muted" />
            </span>
            <input
              type="email"
              className={`form-control ${errors.email ? 'is-invalid' : ''}`}
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="student@example.com"
            />
            {errors.email && (
              <div className="invalid-feedback">{errors.email}</div>
            )}
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-md-4 mb-3">
          <label className="form-label fw-semibold">NSN (National Student Number)</label>
          <input
            type="text"
            className="form-control"
            value={formData.nsn || ''}
            onChange={(e) => handleChange('nsn', e.target.value)}
            placeholder="National Student Number (if known)"
          />
          <small className="text-muted d-block mt-1">(if you have studied in New Zealand before, provide NSN)</small>
        </div>

        <div className="col-md-4 mb-3">
          <label className="form-label fw-semibold">
            Ethnicity <span className="text-danger">*</span>
          </label>
          <select
            className={`form-select ${errors.ethnicity ? 'is-invalid' : ''}`}
            value={ethnicityDropdownValue}
            onChange={(e) => {
              if (e.target.value === 'Other') {
                // When "Other" is selected, set to empty string to show text input
                handleChange('ethnicity', '');
              } else if (e.target.value) {
                handleChange('ethnicity', e.target.value);
              } else {
                handleChange('ethnicity', '');
              }
            }}
          >
            <option value="">Select ethnicity</option>
            {ETHNICITIES.map(ethnicity => (
              <option key={ethnicity} value={ethnicity}>{ethnicity}</option>
            ))}
          </select>
          {errors.ethnicity && (
            <div className="invalid-feedback d-block">{errors.ethnicity}</div>
          )}
          {!showOtherInput && (
            <small className="text-muted d-block mt-1">(e.g. European, Asian, Polynesian)</small>
          )}
          {showOtherInput && (
            <>
              <input
                type="text"
                className="form-control mt-2"
                value={formData.ethnicity || ''}
                onChange={(e) => handleChange('ethnicity', e.target.value)}
                placeholder="Please specify ethnicity"
              />
              <small className="text-muted d-block mt-1">(e.g. European, Asian, Polynesian)</small>
            </>
          )}
        </div>

      </div>

      <div className="row">
        <div className="col-md-4 mb-3">
          <label className="form-label fw-semibold">
            Date of Birth <span className="text-danger">*</span>
          </label>
          <div className="input-group">
            <span className="input-group-text bg-light">
              <Calendar size={18} className="text-muted" />
            </span>
            <input
              type="date"
              className={`form-control ${errors.dateOfBirth ? 'is-invalid' : ''}`}
              value={formData.dateOfBirth || ''}
              onChange={(e) => handleChange('dateOfBirth', e.target.value)}
            />
          </div>
          {errors.dateOfBirth && (
            <div className="invalid-feedback d-block">{errors.dateOfBirth}</div>
          )}
        </div>

        <div className="col-md-4 mb-3">
          <label className="form-label fw-semibold">
            Gender <span className="text-danger">*</span>
          </label>
          <select
            className={`form-select ${errors.gender ? 'is-invalid' : ''}`}
            value={formData.gender || ''}
            onChange={(e) => handleChange('gender', e.target.value)}
          >
            <option value="">Select gender</option>
            {GENDERS.map(gender => (
              <option key={gender} value={gender}>{gender}</option>
            ))}
          </select>
          {errors.gender && (
            <div className="invalid-feedback d-block">{errors.gender}</div>
          )}
        </div>

        <div className="col-md-4 mb-3">
          <label className="form-label fw-semibold">
            Marital Status <span className="text-danger">*</span>
          </label>
          <select
            className={`form-select ${errors.maritalStatus ? 'is-invalid' : ''}`}
            value={formData.maritalStatus || ''}
            onChange={(e) => handleChange('maritalStatus', e.target.value)}
          >
            <option value="">Select marital status</option>
            {MARITAL_STATUSES.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
          {errors.maritalStatus && (
            <div className="invalid-feedback d-block">{errors.maritalStatus}</div>
          )}
        </div>
      </div>

      <div className="row">
        <div className="col-md-6 mb-3">
          <label className="form-label fw-semibold">
            Country of Citizenship <span className="text-danger">*</span>
          </label>
          <div className="input-group">
            <span className="input-group-text bg-light">
              <Globe size={18} className="text-muted" />
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Select
                value={selectedCountryOption}
                onChange={(selectedOption) =>
                  handleChange('countryOfCitizenship', selectedOption?.value || '')
                }
                options={COUNTRY_OPTIONS}
                isSearchable={true}
                isClearable={true}
                placeholder="Select country"
                classNamePrefix="react-select"
                styles={{
                  control: (base, state) => ({
                    ...base,
                    border: errors.countryOfCitizenship
                      ? '1px solid #dc3545'
                      : state.isFocused
                      ? '1px solid #86b7fe'
                      : '1px solid #ced4da',
                    boxShadow: errors.countryOfCitizenship
                      ? '0 0 0 0.25rem rgba(220, 53, 69, 0.25)'
                      : state.isFocused
                      ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)'
                      : 'none',
                    minHeight: '38px',
                  }),
                  valueContainer: (base) => ({
                    ...base,
                    padding: '0.375rem 0.75rem',
                  }),
                }}
              />
            </div>
          </div>
          {errors.countryOfCitizenship && (
            <div className="invalid-feedback d-block">{errors.countryOfCitizenship}</div>
          )}
        </div>

        <div className="col-md-6 mb-3">
          <label className="form-label fw-semibold">
            First Language <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className={`form-control ${errors.firstLanguage ? 'is-invalid' : ''}`}
            value={formData.firstLanguage || ''}
            onChange={(e) => handleChange('firstLanguage', e.target.value)}
            placeholder="e.g., English, Spanish, French"
          />
          {errors.firstLanguage && (
            <div className="invalid-feedback d-block">{errors.firstLanguage}</div>
          )}
        </div>
      </div>

      <div className="row">
        <div className="col-md-6 mb-3">
          <label className="form-label fw-semibold">
            Passport Expiry Date <span className="text-danger">*</span>
          </label>
          <input
            type="date"
            className={`form-control ${errors.passportExpiryDate ? 'is-invalid' : ''}`}
            value={formData.passportExpiryDate || ''}
            onChange={(e) => handleChange('passportExpiryDate', e.target.value)}
          />
          {errors.passportExpiryDate && (
            <div className="invalid-feedback d-block">{errors.passportExpiryDate}</div>
          )}
        </div>
      </div>

      <div className="row">
        <div className="col-12 mb-3">
          <label className="form-label fw-semibold">
            Prior Activity <span className="text-danger">*</span>
          </label>
          <textarea
            className={`form-control ${errors.priorActivity ? 'is-invalid' : ''}`}
            rows={2}
            value={formData.priorActivity || ''}
            onChange={(e) => handleChange('priorActivity', e.target.value)}
            placeholder={`e.g. Student, Worker, Retired, Overseas, etc.`}
          />
          {errors.priorActivity && (
            <div className="invalid-feedback d-block">{errors.priorActivity}</div>
          )}
          <small className="text-muted d-block mt-1">What was your MAIN activity or occupation on 1 October last year? <strong>(Write 'Overseas' if you were not in {institutionCountry})</strong></small>
        </div>
      </div>

      <div className="row">
        <div className="col-md-6 mb-3">
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id="disability"
              checked={formData.disability}
              onChange={(e) => handleChange('disability', e.target.checked)}
            />
            <label className="form-check-label" htmlFor="disability">
              Do you live with the effects of significant injury, mental, behavioural, medical condition, physical illness, long term illness or disability which may affect your ability to study?
            </label>
          </div>
          {formData.disability && (
            <textarea
              className="form-control mt-2"
              rows={3}
              value={formData.disabilityDetails || ''}
              onChange={(e) => handleChange('disabilityDetails', e.target.value)}
              placeholder="Please provide details"
            />
          )}
        </div>

        <div className="col-md-6 mb-3">
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id="convictionChecks"
              checked={formData.convictionChecks}
              onChange={(e) => handleChange('convictionChecks', e.target.checked)}
            />
            <label className="form-check-label" htmlFor="convictionChecks">
              Is there any other information that we need to be aware of that may impact on your study in relation to criminal proceedings / convictions either in your own country and / in {institutionCountry}?
            </label>
          </div>
          {formData.convictionChecks && (
            <textarea
              className="form-control mt-2"
              rows={3}
              value={formData.convictionDetails || ''}
              onChange={(e) => handleChange('convictionDetails', e.target.value)}
              placeholder="Please provide details on a separate document"
            />
          )}
        </div>
      </div>
    </div>
  );
};

